/**
 * Single source of truth: Service- und Projekt-Workflow-Status (deutsche Labels).
 * Backend (Workflow, Transition, Zod) und Frontend (Anzeige, Filter) nutzen dieselben Werte.
 */

/** @type {readonly string[]} */
export const SERVICE_PROJEKT_STATUS_VALUES = Object.freeze([
  "Offen",
  "Zugeteilt",
  "In Arbeit",
  "Arbeiten erledigt",
  "Rechnung geschickt",
  "Abgeschlossen",
  "Storniert",
  "Archiviert",
]);

export const SERVICE_PROJEKT_STATUS = Object.freeze({
  OFFEN: "Offen",
  ZUGE_TEILT: "Zugeteilt",
  IN_ARBEIT: "In Arbeit",
  ARBEITEN_ERLEDIGT: "Arbeiten erledigt",
  RECHNUNG_GESCHICKT: "Rechnung geschickt",
  ABGESCHLOSSEN: "Abgeschlossen",
  STORNIERT: "Storniert",
  ARCHIVIERT: "Archiviert",
});

/** Erlaubte Übergänge: aktueller Status → erlaubte Ziel-Status (Transition Engine) */
export const SERVICE_PROJEKT_ALLOWED_TRANSITIONS = Object.freeze({
  [SERVICE_PROJEKT_STATUS.OFFEN]: [
    SERVICE_PROJEKT_STATUS.ZUGE_TEILT,
    SERVICE_PROJEKT_STATUS.IN_ARBEIT,
    SERVICE_PROJEKT_STATUS.STORNIERT,
    SERVICE_PROJEKT_STATUS.ARCHIVIERT,
  ],
  [SERVICE_PROJEKT_STATUS.ZUGE_TEILT]: [
    SERVICE_PROJEKT_STATUS.IN_ARBEIT,
    SERVICE_PROJEKT_STATUS.STORNIERT,
    SERVICE_PROJEKT_STATUS.ARCHIVIERT,
  ],
  [SERVICE_PROJEKT_STATUS.IN_ARBEIT]: [
    SERVICE_PROJEKT_STATUS.ARBEITEN_ERLEDIGT,
    SERVICE_PROJEKT_STATUS.STORNIERT,
    SERVICE_PROJEKT_STATUS.ARCHIVIERT,
  ],
  [SERVICE_PROJEKT_STATUS.ARBEITEN_ERLEDIGT]: [
    SERVICE_PROJEKT_STATUS.RECHNUNG_GESCHICKT,
    SERVICE_PROJEKT_STATUS.ABGESCHLOSSEN,
    SERVICE_PROJEKT_STATUS.ARCHIVIERT,
  ],
  [SERVICE_PROJEKT_STATUS.RECHNUNG_GESCHICKT]: [
    SERVICE_PROJEKT_STATUS.ABGESCHLOSSEN,
    SERVICE_PROJEKT_STATUS.ARCHIVIERT,
  ],
  [SERVICE_PROJEKT_STATUS.ABGESCHLOSSEN]: [SERVICE_PROJEKT_STATUS.ARCHIVIERT],
  [SERVICE_PROJEKT_STATUS.STORNIERT]: [SERVICE_PROJEKT_STATUS.ARCHIVIERT],
  [SERVICE_PROJEKT_STATUS.ARCHIVIERT]: [],
});

/** Alias → kanonischer Status (Kleinbuchschreibung der Rohwerte) */
export const SERVICE_PROJEKT_STATUS_ALIAS_ENTRIES = Object.freeze([
  ["offen", "Offen"],
  ["neu", "Zugeteilt"],
  ["zugeteilt", "Zugeteilt"],
  ["in arbeit", "In Arbeit"],
  ["bearbeitung", "In Arbeit"],
  ["arbeiten erledigt", "Arbeiten erledigt"],
  ["erledigt", "Arbeiten erledigt"],
  ["rechnung geschickt", "Rechnung geschickt"],
  ["rechnung versendet", "Rechnung geschickt"],
  ["rechnung gesendet", "Rechnung geschickt"],
  ["rechnung", "Rechnung geschickt"],
  ["pendent", "In Arbeit"],
  ["abgeschlossen", "Abgeschlossen"],
  ["abnahme", "Abgeschlossen"],
  ["abgenommen", "Abgeschlossen"],
  ["storniert", "Storniert"],
  ["archiviert", "Archiviert"],
  ["archiv", "Archiviert"],
]);

export function buildServiceProjektStatusAliasMap() {
  return new Map(SERVICE_PROJEKT_STATUS_ALIAS_ENTRIES);
}

/** Für Zod z.enum(): mindestens zwei Einträge als Tupel-Typ in TS; zur Laufzeit volles Array */
export function getServiceProjektStatusEnumTuple() {
  const a = SERVICE_PROJEKT_STATUS_VALUES;
  if (a.length < 2) throw new Error("SERVICE_PROJEKT_STATUS_VALUES needs at least 2 entries");
  return /** @type {[string, string, ...string[]]} */ ([a[0], a[1], ...a.slice(2)]);
}
